import { ReportData } from './types';
import { pb } from '../../lib/pocketbase';

function parseAddress(address: string | undefined | null): { street: string; postcodeCity: string } {
  if (!address) {
    return {
      street: '',
      postcodeCity: ''
    };
  }
  const parts = address.split(',').map(part => part.trim());
  return {
    street: parts[0] || '',
    postcodeCity: parts[1] || ''
  };
}

export const generateReportHTML = async ({ audit, massnahmen, settings, options, previousAuditDate }: ReportData): Promise<string> => {
  const logoUrl = settings?.logo ? pb.files.getURL(settings, settings.logo) : null;

  // Load auditors if not expanded
  let auditors = audit.expand?.auditor || [];
  if ((!auditors || auditors.length === 0) && Array.isArray(audit.auditor) && audit.auditor.length > 0) {
    try {
      const filter = audit.auditor.map((id: string) => `id="${id}"`).join(' || ');
      const result = await pb.collection('users').getList(1, 100, {
        filter,
        fields: 'id,name,email,avatar'
      });
      auditors = (result.items as any[]) || [];
    } catch (err) {
      console.error('Error loading auditors for report:', err);
      auditors = [];
    }
  }

  // Format auditors - only names, no avatars
  const auditorsHtml = auditors.length > 0
    ? auditors.map((auditor, index) => {
        const displayName = auditor.name?.trim() || auditor.email?.trim() || 'Unbekannt';
        return `${displayName}${index < auditors.length - 1 ? ',' : ''}`;
      }).join(' ')
    : 'Nicht verfügbar';

  const kontrollpunkteText = audit.expand?.kontrollpunkt
    ?.map(punkt => punkt.sicherheitssystem)
    .join(', ') || '';

  const address = parseAddress(audit.adresse);

  // Get direktion name through dienststelle
  const begleitpersonText = audit.begleitpersonen?.trim() || 'Begleitperson';
  const letzterAuditText = previousAuditDate
    ? new Date(previousAuditDate).toLocaleDateString('de-CH')
    : 'XXXX';

  return `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0/css/all.min.css">
        <style>
          @media print {
            @page {
              size: ${options.orientation};
              margin: 1cm;
            }
            body {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            .page {
              width: 100%;
            }
            .page-break {
              page-break-after: always;
            }
            /* Inline footer shown at bottom of each page section */
            .page-footer-inline {
              margin-top: 20px;
              padding: 6px 0;
              border-top: 1.5px solid #888;
              display: flex;
              flex-direction: row;
              justify-content: flex-start;
              align-items: center;
              gap: 28px;
              font-size: 9.5pt;
              font-family: Arial, sans-serif;
            }
            .cover-footer-blocker {
              display: none !important;
            }
          }

          body { 
            font-family: Arial, sans-serif; 
            margin: 0;
            padding: 15px;
            line-height: 1.4;
            font-size: 10pt;
            max-width: 100%;
            background-color: white;
          }
          
          .header-container {
            position: relative;
            margin-bottom: 20px;
            border-bottom: 1px solid #e0e0e0;
            padding-bottom: 15px;
            min-height: 100px;
            width: 100%;
          }
          
          .logo {
            position: absolute;
            top: 0;
            right: 0;
            max-width: 180px;
            max-height: 100px;
          }
          
          .header-text {
            text-align: left;
            padding-right: 200px;
            margin-top: 30px;
          }
          
          .audit-info { 
            margin: 20px 0;
            background-color: #f8f9fa !important;
            padding: 15px;
            border-radius: 4px;
            box-shadow: 0 1px 2px rgba(0,0,0,0.1);
          }
          
          .audit-info table { 
            width: 100%; 
            border-collapse: collapse;
            font-size: 10pt;
          }
          
          .audit-info td { 
            padding: 4px 8px;
            vertical-align: top;
          }
          
          .audit-info td:first-child {
            width: 150px;
            font-weight: bold;
            color: #2C2E33;
          }
          
          .measures { 
            margin-top: 20px;
            display: grid;
            grid-template-columns: 1fr;
            gap: 15px;
            width: 100%;
          }
          
          .measure { 
            page-break-inside: avoid;
            break-inside: avoid;
            border: 1px solid #e0e0e0;
            border-radius: 4px;
            box-shadow: 0 1px 2px rgba(0,0,0,0.1);
            background: white;
          }
          
          .measure-header { 
            background-color: #f8f9fa !important;
            padding: 8px 12px;
            border-bottom: 1px solid #e0e0e0;
            color: #2C2E33;
            border-radius: 4px 4px 0 0;
            font-size: 10pt;
            display: flex;
            justify-content: space-between;
            align-items: center;
          }

          .measure-title {
            display: flex;
            align-items: center;
            gap: 8px;
            font-weight: bold;
          }

          .measure-meta {
            display: flex;
            align-items: center;
            gap: 16px;
            color: #666;
          }

          .measure-meta-item {
            display: flex;
            align-items: center;
            gap: 4px;
          }
          
          .measure-content { 
            padding: 12px;
          }
          
          .footer { 
            position: running(footer);
            font-size: 8pt;
            color: #666;
            border-top: 1px solid #e0e0e0;
            padding-top: 8px;
            margin-top: 15px;
            width: 100%;
          }
          
          @page {
            @bottom-left {
              content: element(footer);
            }
          }
          
          .attachments {
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
            margin-top: 8px;
          }
          
          img.attachment { 
            max-width: 120px;
            height: auto;
            border: 1px solid #e0e0e0;
            border-radius: 3px;
          }
          
          h1 { 
            color: #2C2E33;
            margin: 0 0 8px 0;
            font-size: 20pt;
          }
          
          h2 { 
            color: #495057;
            margin: 0;
            font-size: 16pt;
          }
          
          h3 { 
            color: #495057;
            border-bottom: 1px solid #e0e0e0;
            padding-bottom: 8px;
            margin: 20px 0 15px 0;
            font-size: 14pt;
            width: 100%;
          }
          
          .status-text {
            color: #666;
          }
          
          .status-erledigt {
            color: #2b8a3e;
          }
          
          .status-offen {
            color: #e03131;
          }
          
          .field-label {
            font-weight: bold;
            color: #495057;
            font-size: 9pt;
            margin: 8px 0 2px 0;
            display: flex;
            align-items: center;
            gap: 4px;
          }
          
          .field-label:first-child {
            margin-top: 0;
          }
          
          .field-content {
            margin-bottom: 8px;
            line-height: 1.4;
          }
          
          .icon {
            width: 14px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
          }
          
          .measure-grid {
            display: grid;
            grid-template-columns: 1fr;
            gap: 12px;
          }

          .standard-text {
            margin: 20px 0;
            padding: 15px;
            background-color: #f8f9fa;
            border-radius: 4px;
            line-height: 1.6;
          }

          .auditors-list {
            display: flex;
            flex-direction: column;
            gap: 4px;
          }

          /* Inline footer – visible on screen and in print */
          .page-footer-inline {
            margin-top: 24px;
            padding: 8px 0;
            border-top: 2px solid #999;
            display: flex;
            flex-direction: row;
            justify-content: flex-start;
            align-items: center;
            gap: 32px;
            font-size: 11px;
            font-family: Arial, sans-serif;
          }
        </style>
      </head>
      <body>
        <!-- Cover Page -->
        <div class="page page-break">
          <div class="header-container">
            ${logoUrl ? `<img src="${logoUrl}" class="logo" alt="Logo">` : ''}
            <div class="header-text">
              <h1>Auditbericht</h1>
              <h2>Audit #${audit.auditnummer}</h2>
            </div>
          </div>
          
          ${settings?.deckblatt ? `
            <div style="margin: 20px 0; text-align: center;">
              <img
                src="${pb.files.getURL(settings, settings.deckblatt)}"
                style="max-width: 26%; height: auto; display: block; margin: 0 auto;"
                alt="Deckblatt"
              >
            </div>
          ` : ''}

          <div class="audit-info">
            <table>
              ${options.showDatum ? `
              <tr>
                <td><i class="fas fa-calendar icon"></i> Datum:</td>
                <td>${new Date(audit.auditdatum).toLocaleDateString('de-CH')}</td>
              </tr>
              ` : ''}
              ${options.showAuditoren ? `
              <tr>
                <td><i class="fas fa-user icon"></i> ${auditors.length === 1 ? 'Auditor:' : 'Auditoren:'}</td>
                <td>
                  <div class="auditors-list" style="display: flex; flex-wrap: wrap; gap: 8px;">
                    ${auditorsHtml}
                  </div>
                </td>
              </tr>
              ` : ''}
              ${options.showDienststelle && audit.expand?.dienststelle?.name ? `
              <tr>
                <td><i class="fas fa-sitemap icon"></i> Dienststelle:</td>
                <td>${audit.expand.dienststelle.name}</td>
              </tr>
              ` : ''}
              ${options.showAdresse && (address.street || address.postcodeCity) ? `
              <tr>
                <td><i class="fas fa-map-marker-alt icon"></i> Adresse:</td>
                <td>
                  ${address.street}<br>
                  ${address.postcodeCity}
                </td>
              </tr>
              ` : ''}
              ${options.showSicherheitssysteme && kontrollpunkteText ? `
              <tr>
                <td><i class="fas fa-shield-alt icon"></i> ${(audit.expand?.kontrollpunkt?.length ?? 0) === 1 ? 'Kontrollpunkt:' : 'Kontrollpunkte:'}</td>
                <td>${kontrollpunkteText}</td>
              </tr>
              ` : ''}
              ${options.showBegleitpersonen && audit.begleitpersonen ? `
              <tr>
                <td><i class="fas fa-users icon"></i> Begleitpersonen:</td>
                <td>${audit.begleitpersonen}</td>
              </tr>
              ` : ''}
            </table>
          </div>

          <!-- White blocker to hide footer on cover page (print only) -->
          <div class="cover-footer-blocker"></div>
        </div>

        <!-- Measures Page -->
        <div class="page">
          ${options.standardText && options.standardText.trim() ? `
          <div class="standard-text" style="margin-bottom: 20px;">
            ${options.standardText
              .replace(/"Begleitperson"/g, begleitpersonText)
              .replace(/\bXXXX\b/g, letzterAuditText)}
          </div>
          ` : ''}
          <h3><i class="fas fa-tasks"></i> Ist-Zustand und Massnahmen</h3>
          <div class="measures">
            ${massnahmen.map((m, idx) => `
              <div class="measure">
                <div class="measure-header">
                  <div class="measure-title">
                    <i class="fas fa-hashtag"></i> ${idx + 1}${m.bereich ? ` - ${m.bereich}` : ''}
                  </div>
                </div>
                <div class="measure-content">
                  <div class="measure-grid">
                    ${options.showIstzustand ? `
                      <div>
                        <div class="field-label">
                          <i class="fas fa-exclamation-triangle"></i> Ist-Zustand:
                        </div>
                        <div class="field-content">${m.istzustand || ''}</div>
                      </div>
                    ` : ''}
                    
                    ${options.showMassnahme ? `
                      <div>
                        <div class="field-label">
                          <i class="fas fa-check-square"></i> Massnahme:
                        </div>
                        <div class="field-content">${m.massnahme || ''}</div>
                      </div>
                    ` : ''}
                  </div>
                  
                  ${options.showBemerkung && m.bemerkung ? `
                    <div class="field-label">
                      <i class="fas fa-comment"></i> Bemerkung:
                    </div>
                    <div class="field-content">${m.bemerkung}</div>
                  ` : ''}

                  ${options.showAnhange && m.anhaenge?.length ? `
                    <div>
                      <div class="field-label">
                          <i class="fas fa-paperclip"></i> ${(m.anhaenge?.length ?? 0) === 1 ? 'Anhang:' : 'Anhänge:'}
                        </div>
                      <div class="attachments">
                        ${m.anhaenge.filter((anhang): anhang is string => typeof anhang === 'string').map(anhang => `
                          <img src="${pb.files.getURL(m, anhang)}" alt="Anhang" class="attachment">
                        `).join('')}
                      </div>
                    </div>
                  ` : ''}
                </div>
              </div>
            `).join('')}
          </div>

          <!-- Footer inline – visible on screen and in print -->
          <div class="page-footer-inline">
            <span><strong>Zuständig:</strong> ___________________________</span>
            <span><strong>Termin:</strong> ___________________________</span>
            <span><strong>Erledigt</strong> ☐</span>
            <span><strong>Laufend</strong> ☐</span>
          </div>
        </div>


        <div class="footer">
          ${settings?.config.title || ''} | Erstellt am ${new Date().toLocaleDateString('de-CH', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })}
        </div>

        <div class="standard-text">
          Rechtliche Grundlagen: Kantonale Verordnung über Arbeitssicherheit und Gesundheitsschutz 143.81, Leistungsvereinbarung, daraus abgeleitet (VUV, UVG, ArGV 3 + 4). Die Belange des vorbeugenden Brandschutzes wurden nur soweit beurteilt, als diese gestützt auf die erwähnten Rechtsgrundlagen erlassen wurden.
        </div>

        <style>
          @media screen {
            .cover-footer-blocker {
              display: none;
            }
          }
        </style>
      </body>
    </html>
  `;
};
